import React, { useState, useEffect } from 'react';
import { CreateCategoryRequest } from '../models/ServiceProviderType';
import { generateTagFromName } from '../lib/utils';

interface CreateCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCategoryRequest) => Promise<void>;
  serviceProviderTypeTag: string;
  serviceProviderTypeName: string;
  loading?: boolean;
}

export const CreateCategoryModal: React.FC<CreateCategoryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  serviceProviderTypeTag,
  serviceProviderTypeName,
  loading = false,
}) => {
  const [formData, setFormData] = useState<CreateCategoryRequest>({
    serviceProviderTypeTag,
    tag: '',
    name: '',
    description: '',
    icon: '',
    sortOrder: 1,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auto-generate tag when name changes
  useEffect(() => {
    if (formData.name.trim()) {
      const generatedTag = generateTagFromName(formData.name);
      setFormData(prev => ({ ...prev, tag: generatedTag }));
    }
  }, [formData.name]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.tag.trim()) {
      newErrors.tag = 'Tag is required';
    } else if (!/^[a-z0-9_-]+$/.test(formData.tag)) {
      newErrors.tag = 'Tag must contain only lowercase letters, numbers, hyphens, and underscores';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.icon.trim()) {
      newErrors.icon = 'Icon is required';
    }

    if (formData.sortOrder < 1) {
      newErrors.sortOrder = 'Sort order must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      handleClose();
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  const handleClose = () => {
    setFormData({
      serviceProviderTypeTag,
      tag: '',
      name: '',
      description: '',
      icon: '',
      sortOrder: 1,
    });
    setErrors({});
    onClose();
  };

  const handleInputChange = (field: keyof CreateCategoryRequest, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen || !serviceProviderTypeTag) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-md sm:max-w-lg shadow-lg rounded-md bg-white m-4 sm:m-0">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Add New Category
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Service Provider Type:</span> {serviceProviderTypeName}
            </p>
            <p className="text-sm text-blue-700">
              <span className="font-medium">Tag:</span> <span className="font-mono bg-blue-100 px-2 py-1 rounded">{serviceProviderTypeTag}</span>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="tag" className="block text-sm font-medium text-gray-700">
                Category Tag * <span className="text-xs text-gray-500">(auto-generated)</span>
              </label>
              <input
                type="text"
                id="tag"
                value={formData.tag}
                onChange={(e) => handleInputChange('tag', e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 bg-gray-50 ${
                  errors.tag ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Auto-generated from name"
                disabled={loading}
                readOnly
              />
              {errors.tag && <p className="mt-1 text-sm text-red-600">{errors.tag}</p>}
              <p className="mt-1 text-xs text-gray-500">Tag is automatically generated from the name above</p>
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Category Name *
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., Wiring Services"
                disabled={loading}
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description *
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 ${
                  errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., Electrical wiring installation and repair"
                disabled={loading}
              />
              {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
            </div>

            <div>
              <label htmlFor="icon" className="block text-sm font-medium text-gray-700">
                Icon *
              </label>
              <input
                type="text"
                id="icon"
                value={formData.icon}
                onChange={(e) => handleInputChange('icon', e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 ${
                  errors.icon ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., 🔌"
                disabled={loading}
              />
              {errors.icon && <p className="mt-1 text-sm text-red-600">{errors.icon}</p>}
            </div>

            <div>
              <label htmlFor="sortOrder" className="block text-sm font-medium text-gray-700">
                Sort Order *
              </label>
              <input
                type="number"
                id="sortOrder"
                value={formData.sortOrder}
                onChange={(e) => handleInputChange('sortOrder', parseInt(e.target.value) || 1)}
                min="1"
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 ${
                  errors.sortOrder ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={loading}
              />
              {errors.sortOrder && <p className="mt-1 text-sm text-red-600">{errors.sortOrder}</p>}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Category'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
